<script setup lang="ts">
import { ref } from "vue";
import { getPasswordHash } from "../../utils/passwoedHash";
import { apiLogin, redirectAfterLogin } from "../../api/superadmin";
import { useRouter } from "vue-router";
import { AdminFetcher } from "../../api/adminFetcher";
import { toastErr, toastOk } from "../../utils/toasts";
import { InformationModal } from "../../utils/programmingModals";

const router = useRouter();
const login = ref("");
const password = ref("");
const tryAction = async () => {
  const hashed = await getPasswordHash(password.value);
  if (mode.value === "login") {
    const res = await apiLogin(login.value, hashed);
    if (res === "ok") {
      const route = await redirectAfterLogin();
      router.push({ name: route });
    } else {
      alert("Неверный логин или пароль");
      login.value = "";
      password.value = "";
    }
  } else {
    const saved = password.value;
    try {
      const res = (await AdminFetcher.post({
        path: "/superadmin/request-access",
        payload: {
          login: login.value,
          password: hashed,
        },
      })) as { message: string };
      toastOk(res.message);
      mode.value = "login";
      login.value = "";
      password.value = "";
      InformationModal({
        title: "Внимание!",
        text: `Сохраните пароль ${saved} в надёжном месте. Смена или просмотр пароля не будут доступны никому.`,
      });
    } catch (err: any) {
      toastErr(err?.data?.message || "Ошибка запроса");
    }
  }
};
const mode = ref("login"); //login, request
const changeMode = () => {
  password.value = "";
  login.value = "";
  if (mode.value === "login") {
    mode.value = "request";
    password.value = generatePassword();
  } else {
    mode.value = "login";
  }
};

function generatePassword(length = 12) {
  const upperCaseChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowerCaseChars = "abcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const specialChars = "!@#$%^&*()_+[]{}|;:,.<>?";

  const allChars = upperCaseChars + lowerCaseChars + digits + specialChars;
  let password = "";

  // Ensure at least one character from each category is included
  password += upperCaseChars[Math.floor(Math.random() * upperCaseChars.length)];
  password += lowerCaseChars[Math.floor(Math.random() * lowerCaseChars.length)];
  password += digits[Math.floor(Math.random() * digits.length)];
  password += specialChars[Math.floor(Math.random() * specialChars.length)];

  // Fill the rest of the password length
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  // Shuffle the password to prevent it from being predictable
  return password
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
}
</script>
<template>
  <div class="container">
    <div class="form-wrapper">
      <h2>
        {{
          mode === "login"
            ? "Кабинет Администратора"
            : "Запрость доступ к кабинету"
        }}
      </h2>
      <form @submit.prevent="tryAction">
        <div class="input-group">
          <label for="login">Логин</label>
          <input
            type="text"
            id="login"
            name="login"
            v-model="login"
            placeholder="Введите логин"
            required
          />
        </div>
        <div class="input-group">
          <label for="password">Пароль</label>
          <input
            v-if="mode === 'login'"
            type="password"
            id="password"
            name="password"
            v-model="password"
            placeholder="Введите пароль"
            required
          />
          <template v-else-if="mode === 'request'">
            <div class="inp-password">
              <input
                type="text"
                id="password"
                name="password"
                :value="password"
                disabled
                placeholder="Введите пароль"
                required
              />
              <svg
                fill="#000000"
                width="24px"
                height="24px"
                viewBox="0 0 256 256"
                xmlns="http://www.w3.org/2000/svg"
                @click="password = generatePassword()"
              >
                <g fill-rule="evenodd">
                  <path
                    d="M47.895 88.097c.001-4.416 3.064-9.837 6.854-12.117l66.257-39.858c3.785-2.277 9.915-2.277 13.707.008l66.28 39.934c3.786 2.28 6.853 7.703 6.852 12.138l-.028 79.603c-.001 4.423-3.069 9.865-6.848 12.154l-66.4 40.205c-3.781 2.29-9.903 2.289-13.69-.01l-66.167-40.185c-3.78-2.295-6.842-7.733-6.84-12.151l.023-79.72zm13.936-6.474l65.834 36.759 62.766-36.278-62.872-36.918L61.83 81.623zM57.585 93.52c0 1.628-1.065 71.86-1.065 71.86-.034 2.206 1.467 4.917 3.367 6.064l61.612 37.182.567-77.413s-64.48-39.322-64.48-37.693zm76.107 114.938l60.912-38.66c2.332-1.48 4.223-4.915 4.223-7.679V93.125l-65.135 37.513v77.82z"
                  />
                  <path
                    d="M77.76 132.287c-4.782 2.762-11.122.735-14.16-4.526-3.037-5.261-1.622-11.765 3.16-14.526 4.783-2.762 11.123-.735 14.16 4.526 3.038 5.261 1.623 11.765-3.16 14.526zm32 21c-4.782 2.762-11.122.735-14.16-4.526-3.037-5.261-1.622-11.765 3.16-14.526 4.783-2.762 11.123-.735 14.16 4.526 3.038 5.261 1.623 11.765-3.16 14.526zm-32 16c-4.782 2.762-11.122.735-14.16-4.526-3.037-5.261-1.622-11.765 3.16-14.526 4.783-2.762 11.123-.735 14.16 4.526 3.038 5.261 1.623 11.765-3.16 14.526zm32 21c-4.782 2.762-11.122.735-14.16-4.526-3.037-5.261-1.622-11.765 3.16-14.526 4.783-2.762 11.123-.735 14.16 4.526 3.038 5.261 1.623 11.765-3.16 14.526zm78.238-78.052c-4.783-2.762-11.122-.735-14.16 4.526-3.037 5.261-1.623 11.765 3.16 14.526 4.783 2.762 11.123.735 14.16-4.526 3.038-5.261 1.623-11.765-3.16-14.526zm-16.238 29c-4.782-2.762-11.122-.735-14.16 4.526-3.037 5.261-1.622 11.765 3.16 14.526 4.783 2.762 11.123.735 14.16-4.526 3.038-5.261 1.623-11.765-3.16-14.526zm-17 28c-4.782-2.762-11.122-.735-14.16 4.526-3.037 5.261-1.622 11.765 3.16 14.526 4.783 2.762 11.123.735 14.16-4.526 3.038-5.261 1.623-11.765-3.16-14.526zM128.5 69c-6.351 0-11.5 4.925-11.5 11s5.149 11 11.5 11S140 86.075 140 80s-5.149-11-11.5-11z"
                  />
                </g>
              </svg>
            </div>
          </template>
        </div>
        <button type="submit" class="button">
          {{ mode === "login" ? "Войти" : "Отправить" }}
        </button>
        <button
          v-if="mode === 'login'"
          type="button"
          class="link"
          @click="changeMode"
        >
          Запросить доступ
        </button>
        <button
          v-if="mode === 'request'"
          type="button"
          class="link"
          @click="changeMode"
        >
          Вернуться
        </button>
      </form>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.inp-password {
  position: relative;
}

.inp-password svg {
  position: absolute;
  right: 8px;
  top: 7px;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  cursor: pointer;
}

button.link {
  background: none;
  border: none;
  color: #5b9bd5;
  padding: 0;
  cursor: pointer;
  font: inherit;
  text-align: center;
  display: block;
  width: 100%;
  margin: 0;
  margin-top: 8px;

  &:hover {
    color: #4a8ac2;
  }
}

.container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.form-wrapper {
  background: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 350px;
}

.form-wrapper h2 {
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  color: #333;
}

.input-group {
  margin-bottom: 1rem;
}

.input-group label {
  display: block;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  color: #555;
}

.input-group input {
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: border-color 0.3s;
}

.input-group input:focus {
  border-color: #5b9bd5;
  outline: none;
}

.button {
  width: 100%;
  padding: 0.8rem;
  border: none;
  background-color: #5b9bd5;
  color: #fff;
  font-size: 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.button:hover {
  background-color: #4a8ac2;
}
</style>
